import { useEffect, useMemo, useState } from "react";
import { useCustom, useGetLocale, useTranslate } from "@refinedev/core";
import {
  ArrowDownToLine,
  CalendarDays,
  ChevronDown,
  CreditCard,
  ExternalLink,
  Euro,
  FileText,
  LayoutGrid,
  List,
  MoreVertical,
  PawPrint,
  Receipt,
  RotateCcw,
  RotateCw,
  Rows3,
  Settings2,
  Sliders,
  Store,
  Tag,
  User,
  XCircle,
} from "lucide-react";
import { Button, Field, SearchIcon, SelectField, StatusBadge, type StatusTone } from "@fawkes/ui";

import { downloadFactuurPdf } from "../api/factuurExport";
import { formatDate, formatEuro } from "../i18n/format";
import { IndeelbareTabel } from "../components/IndeelbareTabel";
import { PeriodeFilter, periodeBereik } from "../components/PeriodeFilter";

/**
 * Financieel → Betalingen (maintainer design 2026-08-17): the payment overview, in the same chrome as the
 * invoice overview — KPI strip, filter bar, and the three result presentations behind one switch (table,
 * small tiles, large tiles). Same classes as that screen on purpose: these are two tabs of one screen, and
 * a second set of nearly-identical styles is how two tabs start drifting apart.
 *
 * <p>Every control is a server parameter ({@code GET /api/betalingen/overzicht}), so the page, the "N
 * resultaten" count and the KPI amounts always describe one and the same set.
 *
 * <p><b>Status is derived</b> — {@code betaling} has no status column. The four cases the data supports are
 * received / outstanding ({@code betaal_wijze = 'Niet voldaan'}) / refunded (negative amount) / cancelled
 * ({@code datum_afboeken} set); see the api's {@code BetalingOverzichtRepository}. The mock's "In
 * behandeling" and "Mislukt" have no counterpart in this schema, so they are not offered as filters —
 * a filter that can only ever return nothing is worse than one option less.
 */

interface BetalingListItem {
  id: number;
  referentie: string | null;
  datum: string | null;
  klantId: number | null;
  klantNaam: string | null;
  factuurId: number | null;
  factuurNummer: string | null;
  onderdeel: string | null;
  betaalwijze: string | null;
  soort: string | null;
  status: BetalingStatus;
  bedrag: number | null;
  dierNaam: string | null;
  vestigingNaam: string | null;
  vervalDatum: string | null;
  factuurTotaal: number | null;
  factuurBetaald: number | null;
  factuurOpenstaand: number | null;
}

type BetalingStatus = "ONTVANGEN" | "OPENSTAAND" | "TERUGBETAALD" | "GEANNULEERD";

interface BetalingPage {
  items: BetalingListItem[];
  total: number;
  page: number;
  size: number;
  totalen?: { ontvangen: number; openstaand: number; terugbetaald: number; geannuleerd: number } | null;
  /** The payment methods actually in use, so the filter offers real values. */
  betaalwijzen?: string[] | null;
}

const PAGE_SIZE = 24;

const STATUS_TONE: Record<BetalingStatus, StatusTone> = {
  ONTVANGEN: "success",
  OPENSTAAND: "warning",
  TERUGBETAALD: "info",
  GEANNULEERD: "danger",
};

const STATUSSEN: BetalingStatus[] = ["ONTVANGEN", "OPENSTAAND", "TERUGBETAALD", "GEANNULEERD"];

/** The switchable columns; the rest of the table (reference, date, relation, status, amount) is always on. */
const KOLOMMEN = ["factuur", "onderdeel", "betaalmethode", "dier", "vestiging", "soort",
  "factuurTotaal", "factuurBetaald", "factuurOpenstaand", "vervaldatum"] as const;
type Kolom = (typeof KOLOMMEN)[number];
const KOLOM_KEY = "fawkes.betalingen.kolommen";
const STANDAARD: Kolom[] = ["factuur", "onderdeel", "betaalmethode"];

function leesKolommen(): Kolom[] {
  try {
    const raw = globalThis.localStorage?.getItem(KOLOM_KEY);
    if (!raw) return [...STANDAARD];
    const gekozen = JSON.parse(raw) as unknown;
    return Array.isArray(gekozen) ? KOLOMMEN.filter((k) => gekozen.includes(k)) : [...STANDAARD];
  } catch {
    return [...STANDAARD];
  }
}

type Weergave = "tabel" | "klein" | "groot";
type Sortering = "nieuwste" | "oudste" | "bedrag";

export function BetalingOverzicht({ onOpenFactuur }: { onOpenFactuur?: (id: number) => void } = {}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const [weergave, setWeergave] = useState<Weergave>("tabel");
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [periode, setPeriode] = useState("maand");
  const [eigenPeriode, setEigenPeriode] = useState({ van: "", tot: "" });
  const [status, setStatus] = useState("");
  const [betaalwijze, setBetaalwijze] = useState("");
  const [vestigingen, setVestigingen] = useState<string[]>([]);
  const [vestigingOpen, setVestigingOpen] = useState(false);
  const [vestigingZoek, setVestigingZoek] = useState("");
  const [onderdeel, setOnderdeel] = useState("");
  const [sortering, setSortering] = useState<Sortering>("nieuwste");
  const [bedragVanaf, setBedragVanaf] = useState("");
  const [alleenOpenstaand, setAlleenOpenstaand] = useState(false);
  const [page, setPage] = useState(1);
  const [kolommen, setKolommen] = useState<Kolom[]>(leesKolommen);
  const [veldenOpen, setVeldenOpen] = useState(false);
  const [menuVoor, setMenuVoor] = useState<number | null>(null);
  const toonKolom = (k: Kolom) => kolommen.includes(k);
  const [pdfFout, setPdfFout] = useState(false);
  // Geauthenticeerd ophalen; een kale <a href> naar /api/facturen/{id}/pdf gaf een 401 (zie
  // factuurExport: het token zit alleen in het geheugen, niet in een cookie).
  const pdf = (factuurId: number, nummer: string | null) => {
    setMenuVoor(null);
    setPdfFout(false);
    downloadFactuurPdf(String(factuurId), nummer || String(factuurId)).catch(() => setPdfFout(true));
  };
  const zetKolommen = (next: Kolom[]) => {
    setKolommen(next);
    try { globalThis.localStorage?.setItem(KOLOM_KEY, JSON.stringify(next)); } catch { /* niet erg */ }
  };
  const wisselKolom = (k: Kolom) =>
    zetKolommen(kolommen.includes(k) ? kolommen.filter((x) => x !== k) : [...kolommen, k]);

  useEffect(() => {
    const handle = setTimeout(() => { setDebounced(query.trim()); setPage(1); }, 300);
    return () => clearTimeout(handle);
  }, [query]);
  useEffect(() => {
    setPage(1);
  }, [periode, status, betaalwijze, vestigingen, onderdeel, bedragVanaf, alleenOpenstaand]);

  const bereik = useMemo(() => periodeBereik(periode, new Date(), eigenPeriode), [periode, eigenPeriode]);

  // Klik ernaast (of Escape) klapt de open menu's dicht — net als bij Facturen.
  useEffect(() => {
    if (!vestigingOpen && !veldenOpen && menuVoor == null) return;
    const sluit = () => { setVestigingOpen(false); setVeldenOpen(false); setMenuVoor(null); };
    const buiten = (e: MouseEvent) => {
      const doel = e.target as HTMLElement | null;
      if (doel?.closest(".fawkes-fin__multi, .fawkes-fin__velden, .fawkes-fin__rowmenu")) return;
      sluit();
    };
    const toets = (e: KeyboardEvent) => { if (e.key === "Escape") sluit(); };
    document.addEventListener("mousedown", buiten);
    document.addEventListener("keydown", toets);
    return () => {
      document.removeEventListener("mousedown", buiten);
      document.removeEventListener("keydown", toets);
    };
  }, [vestigingOpen, veldenOpen, menuVoor]);

  const url = useMemo(() => {
    const params = new URLSearchParams();
    if (debounced) params.set("q", debounced);
    if (bereik.from) params.set("from", bereik.from);
    if (bereik.to) params.set("to", bereik.to);
    vestigingen.forEach((v) => params.append("vestiging", v));
    if (onderdeel) params.set("onderdeel", onderdeel);
    if (betaalwijze) params.set("betaalwijze", betaalwijze);
    if (status) params.set("status", status);
    const min = bedragVanaf.replace(",", ".").trim();
    if (min && Number.isFinite(Number(min))) params.set("bedragVanaf", min);
    if (alleenOpenstaand) params.set("alleenOpenstaand", "true");
    params.set("sort", sortering);
    params.set("page", String(page));
    params.set("size", String(PAGE_SIZE));
    return `betalingen/overzicht?${params.toString()}`;
  }, [debounced, bereik, vestigingen, onderdeel, betaalwijze, status, bedragVanaf, alleenOpenstaand,
    sortering, page]);

  const { data: vestigingFeed } = useCustom<{ id: number; naam: string }[]>({
    url: "vestigingen", method: "get",
  });
  const alleVestigingen = useMemo(() => {
    const rijen = vestigingFeed?.data;
    const namen = Array.isArray(rijen) ? rijen.map((v) => (v?.naam ?? "").trim()).filter(Boolean) : [];
    return [...new Set(namen)].sort((a, b) => a.localeCompare(b));
  }, [vestigingFeed]);

  const zichtbareVestigingen = useMemo(() => {
    const naald = vestigingZoek.trim().toLowerCase();
    const gefilterd = naald
      ? alleVestigingen.filter((v) => v.toLowerCase().includes(naald))
      : alleVestigingen;
    const aan = gefilterd.filter((v) => vestigingen.includes(v));
    const uit = gefilterd.filter((v) => !vestigingen.includes(v));
    return [...aan, ...uit];
  }, [alleVestigingen, vestigingZoek, vestigingen]);

  const { data, isLoading, isError, refetch } = useCustom<BetalingPage>({ url, method: "get" });
  const rijen = data?.data?.items ?? [];
  const totaalAantal = data?.data?.total ?? 0;
  const betaalwijzen = useMemo(() => {
    const uit = data?.data?.betaalwijzen;
    return Array.isArray(uit) ? uit : [];
  }, [data]);

  const onderdelen = useMemo(
    () => [...new Set([...rijen.map((r) => r.onderdeel?.trim()).filter(Boolean), onderdeel]
      .filter(Boolean) as string[])].sort(),
    [rijen, onderdeel],
  );
  const heeftDier = useMemo(() => rijen.some((r) => (r.dierNaam ?? "").trim() !== ""), [rijen]);
  const heeftOnderdeel = onderdelen.length > 0;
  const heeftVestiging = alleVestigingen.length > 0;

  const totalen = data?.data?.totalen ?? null;
  const kpi = {
    ontvangen: totalen?.ontvangen ?? 0,
    openstaand: totalen?.openstaand ?? 0,
    terugbetaald: totalen?.terugbetaald ?? 0,
    geannuleerd: totalen?.geannuleerd ?? 0,
  };

  const wissen = () => {
    setQuery(""); setPeriode("maand"); setStatus(""); setBetaalwijze(""); setVestigingen([]);
    setOnderdeel(""); setBedragVanaf(""); setAlleenOpenstaand(false); setSortering("nieuwste");
  };

  return (
    <>
      <header className="fawkes-fin__head">
        <span className="fawkes-fin__head-icon" aria-hidden="true"><CreditCard size={26} /></span>
        <div className="fawkes-fin__head-text">
          <h2>{t("betalingen.overzicht.title")}</h2>
          <p>{t("betalingen.overzicht.subtitle")}</p>
        </div>
        <div className="fawkes-fin__kpis">
          <Kpi icon={<ArrowDownToLine size={18} />} value={formatEuro(kpi.ontvangen, locale)}
            label={t("betalingen.kpi.ontvangen")} />
          <Kpi icon={<Euro size={18} />} value={formatEuro(kpi.openstaand, locale)}
            label={t("betalingen.kpi.openstaand")} />
          <Kpi icon={<RotateCw size={18} />} value={formatEuro(kpi.terugbetaald, locale)}
            label={t("betalingen.kpi.terugbetaald")} />
          <Kpi icon={<XCircle size={18} />} value={formatEuro(kpi.geannuleerd, locale)}
            label={t("betalingen.kpi.geannuleerd")} />
          <Kpi icon={<Receipt size={18} />} value={String(totaalAantal)} label={t("financieel.kpi.totaal")} accent />
        </div>
      </header>

      {/* Six labelled filters here (Betaalmethode as well), so the row gets the six-track variant. */}
      <section className="fawkes-fin__filters fawkes-fin__filters--zes">
        <div className="fawkes-fin__searchcell">
          <span className="fui-field__label" aria-hidden="true">&nbsp;</span>
          <label className="fawkes-fin__search">
            <span className="fawkes-fin__search-icon" aria-hidden="true">
              <SearchIcon width={18} height={18} />
            </span>
            <input
              value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={t("betalingen.filter.zoek")} aria-label={t("betalingen.filter.zoek")}
            />
          </label>
        </div>
        <PeriodeFilter waarde={periode} onWaarde={setPeriode} eigen={eigenPeriode} onEigen={setEigenPeriode} />
        {heeftVestiging && (
          <div className="fawkes-fin__multi">
            <span className="fui-field__label">{t("financieel.filter.vestiging")}</span>
            <button
              type="button" className="fawkes-fin__multi-btn"
              aria-haspopup="true" aria-expanded={vestigingOpen}
              onClick={() => setVestigingOpen((v) => !v)}
            >
              <span>
                {vestigingen.length === 0
                  ? t("financieel.filter.alle")
                  : vestigingen.length === 1
                    ? vestigingen[0]
                    : t("financieel.filter.gekozen", { args: [String(vestigingen.length)] })}
              </span>
              <ChevronDown size={14} aria-hidden="true" />
            </button>
            {vestigingOpen && (
              <div className="fawkes-fin__multi-menu" role="group" aria-label={t("financieel.filter.vestiging")}>
                <label className="fawkes-fin__multi-zoek">
                  <SearchIcon width={14} height={14} aria-hidden="true" />
                  <input
                    value={vestigingZoek} autoFocus
                    placeholder={t("financieel.filter.vestigingZoek")}
                    aria-label={t("financieel.filter.vestigingZoek")}
                    onChange={(e) => setVestigingZoek(e.target.value)}
                  />
                </label>
                <button
                  type="button" className="fawkes-fin__multi-all"
                  onClick={() => setVestigingen((cur) =>
                    zichtbareVestigingen.every((v) => cur.includes(v))
                      ? cur.filter((v) => !zichtbareVestigingen.includes(v))
                      : [...new Set([...cur, ...zichtbareVestigingen])])}
                >
                  {zichtbareVestigingen.length > 0 && zichtbareVestigingen.every((v) => vestigingen.includes(v))
                    ? t("financieel.filter.allesUit")
                    : t("financieel.filter.allesAan")}
                </button>
                <div className="fawkes-fin__multi-list">
                  {zichtbareVestigingen.length === 0 && (
                    <p className="fawkes-fin__multi-leeg">{t("financieel.filter.vestigingGeen")}</p>
                  )}
                  {zichtbareVestigingen.map((v) => (
                    <label key={v}>
                      <input
                        type="checkbox" checked={vestigingen.includes(v)}
                        onChange={() => setVestigingen((cur) =>
                          cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v])}
                      />
                      <span>{v}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <SelectField
          label={t("betalingen.filter.betaalmethode")} value={betaalwijze}
          onChange={(e) => setBetaalwijze(e.target.value)}
          options={[{ value: "", label: t("financieel.filter.alle") },
            ...betaalwijzen.map((v) => ({ value: v, label: v }))]}
        />
        {heeftOnderdeel && <SelectField
          label={t("financieel.filter.onderdeel")} value={onderdeel}
          onChange={(e) => setOnderdeel(e.target.value)}
          options={[{ value: "", label: t("financieel.filter.alle") },
            ...onderdelen.map((v) => ({ value: v, label: v }))]}
        />}
        <SelectField
          label={t("financieel.filter.status")} value={status}
          onChange={(e) => setStatus(e.target.value)}
          options={[{ value: "", label: t("financieel.filter.alle") },
            ...STATUSSEN.map((s) => ({ value: s, label: t(`betalingen.status.${s}`) }))]}
        />
        <Field
          label={t("financieel.filter.bedragVanaf")} type="number" min={0} step={5}
          value={bedragVanaf} placeholder="0"
          onChange={(e) => setBedragVanaf(e.target.value)}
        />
        <div className="fawkes-fin__filters-actions">
          <span className="fui-field__label" aria-hidden="true">&nbsp;</span>
          <Button variant="secondary" onClick={wissen}>
            <RotateCcw size={14} /> {t("financieel.filter.wissen")}
          </Button>
        </div>
        <div className="fawkes-fin__toggle">
          <button
            type="button" role="switch" aria-checked={alleenOpenstaand}
            className={`fawkes-fin__switch${alleenOpenstaand ? " is-on" : ""}`}
            onClick={() => setAlleenOpenstaand((v) => !v)}
          >
            <span className="fawkes-fin__switch-knob" />
          </button>
          <span className="fawkes-fin__toggle-label">{t("betalingen.filter.alleenOpenstaand")}</span>
          <span className="fawkes-fin__toggle-state">
            {t(alleenOpenstaand ? "financieel.filter.actief" : "financieel.filter.inactief")}
          </span>
        </div>
      </section>

      <div className="fawkes-fin__resultbar">
        <span className="fawkes-fin__count">
          <Sliders size={14} aria-hidden="true" />
          {t("financieel.resultaten", { args: [String(totaalAantal)] })}
        </span>
        <div className="fawkes-fin__resultbar-right">
          <div className="fawkes-fin__velden">
            <Button variant="secondary" onClick={() => setVeldenOpen((v) => !v)}>
              <Settings2 size={14} /> {t("financieel.velden")}
            </Button>
            {veldenOpen && (
              <div className="fawkes-fin__velden-menu" role="group" aria-label={t("financieel.velden")}>
                <button
                  type="button" className="fawkes-fin__multi-all"
                  onClick={() => zetKolommen(kolommen.length === KOLOMMEN.length ? [] : [...KOLOMMEN])}
                >
                  {kolommen.length === KOLOMMEN.length
                    ? t("financieel.filter.allesUit")
                    : t("financieel.filter.allesAan")}
                </button>
                <div className="fawkes-fin__velden-list">
                  {KOLOMMEN.map((k) => (
                    <label key={k}>
                      <input type="checkbox" checked={toonKolom(k)} onChange={() => wisselKolom(k)} />
                      <span>{t(`betalingen.kolom.${k}`)}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="fawkes-fin__views" role="group" aria-label={t("financieel.weergave")}>
            <ViewBtn active={weergave === "tabel"} onClick={() => setWeergave("tabel")}
              title={t("financieel.weergave.tabel")}><List size={16} /></ViewBtn>
            <ViewBtn active={weergave === "klein"} onClick={() => setWeergave("klein")}
              title={t("financieel.weergave.klein")}><LayoutGrid size={16} /></ViewBtn>
            <ViewBtn active={weergave === "groot"} onClick={() => setWeergave("groot")}
              title={t("financieel.weergave.groot")}><Rows3 size={16} /></ViewBtn>
          </div>
          <label className="fawkes-fin__sort">
            <span>{t("financieel.sorteren")}</span>
            <select value={sortering} onChange={(e) => setSortering(e.target.value as Sortering)}>
              <option value="nieuwste">{t("financieel.sorteren.nieuwste")}</option>
              <option value="oudste">{t("financieel.sorteren.oudste")}</option>
              <option value="bedrag">{t("financieel.sorteren.bedrag")}</option>
            </select>
          </label>
        </div>
      </div>

      {pdfFout && (
        <p className="fawkes-fin__state fawkes-fin__state--error" role="alert">
          {t("factuurDetailView.notice.exportError")}
        </p>
      )}
      {isLoading && <p className="fawkes-fin__state">{t("common.loading")}</p>}
      {isError && (
        <p className="fawkes-fin__state fawkes-fin__state--error">
          {t("common.loadError")}{" "}
          <Button variant="secondary" onClick={() => refetch()}>{t("common.retry")}</Button>
        </p>
      )}
      {!isLoading && !isError && rijen.length === 0 && (
        <p className="fawkes-fin__state">{t("betalingen.leeg")}</p>
      )}

      {!isLoading && !isError && rijen.length > 0 && weergave === "tabel" && (
        <div className="fawkes-fin__tablewrap">
          <IndeelbareTabel
            tabelKey="betalingoverzicht"
            columns={[
              {
                key: "referentie", header: t("betalingen.kolom.referentie"),
                cell: (r) => (
                  <button type="button" className="fawkes-fin__link"
                    onClick={() => r.factuurId && onOpenFactuur?.(r.factuurId)}>
                    {r.referentie ?? "—"}
                  </button>
                ),
              },
              { key: "datum", header: t("financieel.kolom.datum"),
                cell: (r) => <span className="tabular">{formatDate(dagVan(r.datum), locale)}</span> },
              { key: "relatie", header: t("financieel.kolom.relatie"), cell: (r) => r.klantNaam ?? "—" },
              // De keuzelijst "Velden" bepaalt welke kolommen er staan; dat blijft onveranderd.
              ...(toonKolom("factuur") ? [{
                key: "factuur", header: t("betalingen.kolom.factuur"),
                cell: (r: typeof rijen[number]) => (r.factuurId ? (
                  <button type="button" className="fawkes-fin__link"
                    onClick={() => onOpenFactuur?.(r.factuurId!)}>
                    {r.factuurNummer || t("financieel.concept")}
                  </button>
                ) : "—"),
              }] : []),
              ...(heeftDier && toonKolom("dier") ? [{
                key: "dier", header: t("financieel.kolom.dier"),
                cell: (r: typeof rijen[number]) => r.dierNaam ?? "—",
              }] : []),
              ...(heeftOnderdeel && toonKolom("onderdeel") ? [{
                key: "onderdeel", header: t("financieel.kolom.onderdeel"),
                cell: (r: typeof rijen[number]) => r.onderdeel ?? "—",
              }] : []),
              ...(heeftVestiging && toonKolom("vestiging") ? [{
                key: "vestiging", header: t("financieel.kolom.vestiging"),
                cell: (r: typeof rijen[number]) => r.vestigingNaam ?? "—",
              }] : []),
              ...(toonKolom("betaalmethode") ? [{
                key: "betaalmethode", header: t("betalingen.kolom.betaalmethode"),
                cell: (r: typeof rijen[number]) => r.betaalwijze ?? "—",
              }] : []),
              ...(toonKolom("soort") ? [{
                key: "soort", header: t("betalingen.kolom.soort"),
                cell: (r: typeof rijen[number]) => r.soort ?? "—",
              }] : []),
              ...(toonKolom("vervaldatum") ? [{
                key: "vervaldatum", header: t("financieel.kolom.vervaldatum"),
                cell: (r: typeof rijen[number]) => <span className="tabular">{formatDate(r.vervalDatum, locale)}</span>,
              }] : []),
              {
                key: "status", header: t("financieel.kolom.status"),
                cell: (r) => (
                  <StatusBadge tone={STATUS_TONE[r.status]}>{t(`betalingen.status.${r.status}`)}</StatusBadge>
                ),
              },
              ...(toonKolom("factuurTotaal") ? [{
                key: "factuurTotaal", header: t("betalingen.kolom.factuurTotaal"),
                cell: (r: typeof rijen[number]) => (
                  <span className="is-num tabular">{formatEuro(r.factuurTotaal, locale)}</span>
                ),
              }] : []),
              ...(toonKolom("factuurBetaald") ? [{
                key: "factuurBetaald", header: t("betalingen.kolom.factuurBetaald"),
                cell: (r: typeof rijen[number]) => (
                  <span className="is-num tabular">{formatEuro(r.factuurBetaald, locale)}</span>
                ),
              }] : []),
              ...(toonKolom("factuurOpenstaand") ? [{
                key: "factuurOpenstaand", header: t("betalingen.kolom.factuurOpenstaand"),
                cell: (r: typeof rijen[number]) => (
                  <span className="is-num tabular">{formatEuro(r.factuurOpenstaand, locale)}</span>
                ),
              }] : []),
              { key: "bedrag", header: t("betalingen.kolom.bedrag"),
                cell: (r) => <span className="is-num tabular">{formatEuro(r.bedrag, locale)}</span> },
              {
                key: "acties", header: "", vast: true,
                cell: (r) => (
                  <div className="fawkes-fin__rowmenu">
                    <button
                      type="button" className="fawkes-fin__kebab" title={t("common.more")}
                      aria-haspopup="true" aria-expanded={menuVoor === r.id}
                      onClick={(e) => { e.stopPropagation(); setMenuVoor(menuVoor === r.id ? null : r.id); }}
                    >
                      <MoreVertical size={16} />
                    </button>
                    {menuVoor === r.id && (
                      <div className="fawkes-fin__rowmenu-items" role="menu">
                        <button type="button" role="menuitem" disabled={!r.factuurId}
                          onClick={() => { setMenuVoor(null); if (r.factuurId) onOpenFactuur?.(r.factuurId); }}>
                          {t("betalingen.actie.factuur")}
                        </button>
                        {r.factuurId && (
                          <button type="button" role="menuitem"
                            onClick={() => { if (r.factuurId) pdf(r.factuurId, r.factuurNummer); }}>
                            {t("financieel.actie.pdf")}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ),
              },
            ]}
            rows={rijen}
            getRowKey={(r) => String(r.id)}
            empty=""
          />
        </div>
      )}

      {!isLoading && !isError && rijen.length > 0 && weergave !== "tabel" && (
        <div className={`fawkes-fin__grid fawkes-fin__grid--${weergave}`}>
          {rijen.map((r) => (
            <article key={r.id} className="fawkes-fin__card">
              <header className="fawkes-fin__card-head">
                <span className="fawkes-fin__card-nr">{r.referentie ?? "—"}</span>
                <StatusBadge tone={STATUS_TONE[r.status]}>{t(`betalingen.status.${r.status}`)}</StatusBadge>
                <button type="button" className="fawkes-fin__kebab" title={t("common.more")}
                  onClick={() => r.factuurId && onOpenFactuur?.(r.factuurId)}>
                  <MoreVertical size={16} />
                </button>
              </header>

              <div className="fawkes-fin__card-body">
                <dl>
                  <Rij icon={<User size={13} />} label={t("financieel.kolom.relatie")} value={r.klantNaam} />
                  {weergave === "groot" && (
                    <Rij icon={<FileText size={13} />} label={t("betalingen.kolom.factuur")}
                      value={r.factuurNummer} />
                  )}
                  {heeftDier && (
                    <Rij icon={<PawPrint size={13} />} label={t("financieel.kolom.dier")} value={r.dierNaam} />
                  )}
                  <Rij icon={<CalendarDays size={13} />} label={t("financieel.kolom.datum")}
                    value={formatDate(dagVan(r.datum), locale)} />
                </dl>
                {weergave === "groot" ? (
                  <dl>
                    {heeftVestiging && (
                      <Rij icon={<Store size={13} />} label={t("financieel.kolom.vestiging")}
                        value={r.vestigingNaam} />
                    )}
                    <Rij icon={<Tag size={13} />} label={t("financieel.kolom.onderdeel")} value={r.onderdeel} />
                    <Rij icon={<CreditCard size={13} />} label={t("betalingen.kolom.betaalmethode")}
                      value={r.betaalwijze} />
                    <Rij icon={<Receipt size={13} />} label={t("betalingen.kolom.soort")} value={r.soort} />
                  </dl>
                ) : (
                  <dl>
                    <Rij icon={<Tag size={13} />} label={t("financieel.kolom.onderdeel")} value={r.onderdeel} />
                    <Rij icon={<CreditCard size={13} />} label={t("betalingen.kolom.betaalmethode")}
                      value={r.betaalwijze} />
                  </dl>
                )}
              </div>

              <footer className="fawkes-fin__card-foot">
                <Bedrag label={t("betalingen.kolom.bedrag")} value={formatEuro(r.bedrag, locale)} />
                {weergave === "groot" && (
                  <Bedrag label={t("betalingen.kolom.factuurBetaald")}
                    value={formatEuro(r.factuurBetaald, locale)} />
                )}
                {weergave === "groot" ? (
                  <Bedrag
                    label={t("betalingen.kolom.factuurOpenstaand")}
                    value={formatEuro(r.factuurOpenstaand, locale)}
                    danger={(r.factuurOpenstaand ?? 0) > 0}
                  />
                ) : (
                  <span className={`fawkes-fin__card-status is-${r.status.toLowerCase()}`}>
                    {t(`betalingen.status.${r.status}`)}
                  </span>
                )}
              </footer>

              {weergave === "groot" && (
                <div className="fawkes-fin__card-actions">
                  <Button variant="secondary" disabled={!r.factuurId}
                    onClick={() => r.factuurId && onOpenFactuur?.(r.factuurId)}>
                    {t("financieel.actie.openen")} <ExternalLink size={13} />
                  </Button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {totaalAantal > PAGE_SIZE && (
        <div className="fawkes-fin__pager">
          <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            {t("common.previous")}
          </Button>
          <span className="fawkes-fin__pageinfo">
            {t("financieel.pagina", { args: [String(page), String(Math.ceil(totaalAantal / PAGE_SIZE))] })}
          </span>
          <Button
            variant="secondary" disabled={page >= Math.ceil(totaalAantal / PAGE_SIZE)}
            onClick={() => setPage((p) => p + 1)}
          >
            {t("common.next")}
          </Button>
        </div>
      )}
    </>
  );
}

/**
 * A payment carries a timestamp; formatDate renders a calendar day in UTC (so a bare date never shifts),
 * which would move local midnight back a day. Take the date part and let it print as itself.
 */
function dagVan(iso: string | null | undefined): string | null {
  return iso ? iso.slice(0, 10) : null;
}

function Kpi({ icon, value, label, accent }: {
  icon: React.ReactNode; value: string; label: string; accent?: boolean;
}) {
  return (
    <div className={`fawkes-fin__kpi${accent ? " is-accent" : ""}`}>
      <span className="fawkes-fin__kpi-icon" aria-hidden="true">{icon}</span>
      <span className="fawkes-fin__kpi-text">
        <strong>{value}</strong>
        <small>{label}</small>
      </span>
    </div>
  );
}

function ViewBtn({ active, onClick, title, children }: {
  active: boolean; onClick: () => void; title: string; children: React.ReactNode;
}) {
  return (
    <button
      type="button" className={`fawkes-fin__view${active ? " is-active" : ""}`}
      aria-pressed={active} title={title} aria-label={title} onClick={onClick}
    >
      {children}
    </button>
  );
}

function Rij({ icon, label, value, danger }: {
  icon: React.ReactNode; label: string; value: string | null | undefined; danger?: boolean;
}) {
  return (
    <div className="fawkes-fin__rij">
      <dt><span aria-hidden="true">{icon}</span> {label}</dt>
      <dd className={danger ? "is-danger" : undefined}>{value?.trim() || "—"}</dd>
    </div>
  );
}

function Bedrag({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div className="fawkes-fin__bedrag">
      <small>{label}</small>
      <strong className={danger ? "is-danger" : undefined}>{value}</strong>
    </div>
  );
}
